import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ArticleMeta {
  publishedTime?: string | null;
  modifiedTime?: string | null;
  author?: string | null;
  section?: string | null;
  tags?: string[];
}

interface SeoProps {
  title: string;
  description: string;
  image?: string;
  /** Optional alt text for OG/Twitter image (improves a11y + crawler context). */
  imageAlt?: string;
  url?: string;
  type?: "website" | "article";
  locale?: string;
  siteName?: string;
  /** Article-specific OG metadata (used when type === "article"). */
  article?: ArticleMeta;
  /** Twitter handle for the site (e.g. "@mtbbtp"). */
  twitterSite?: string;
  /** Optional JSON-LD object(s) to inject into <head>. */
  jsonLd?: object | object[];
  /** Empêche l'indexation (pages admin, outils internes, pages de confirmation). */
  noindex?: boolean;
}

const SITE_ORIGIN = "https://www.mtbbtp.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/social-preview-mtb-btp.png`;
const DEFAULT_SITE_NAME = "MTB BTP";
const JSON_LD_ID = "mtb-jsonld";
const ARTICLE_TAG_MARKER = "data-mtb-article-tag";

/** Ensure URLs sent to crawlers are absolute. */
const toAbsoluteUrl = (maybeUrl?: string): string | undefined => {
  if (!maybeUrl) return undefined;
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;
  if (typeof window === "undefined") return maybeUrl;
  try {
    return new URL(maybeUrl, window.location.origin).toString();
  } catch {
    return maybeUrl;
  }
};

const updateMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      if (key !== "content") {
        element.setAttribute(key, value);
      }
    });
    document.head.appendChild(element);
  }

  if (attributes.content) {
    element.setAttribute("content", attributes.content);
  }
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.appendChild(element);
  }
  
  element.href = href;
};

const HREFLANG_MARKER = "data-mtb-hreflang";

/** Inject hreflang alternate links for multilingual SEO. */
const updateHreflangLinks = (baseUrl: string) => {
  // Purge de toutes les alternates linguistiques existantes (statiques d'index.html
  // incluses) afin de ne conserver qu'une seule série de balises hreflang par page.
  document.head
    .querySelectorAll("link[rel=\"alternate\"][hreflang]")
    .forEach((node) => node.remove());

  // Strip existing lang query param to build clean base
  const url = new URL(baseUrl, typeof window !== "undefined" ? window.location.origin : "https://www.mtbbtp.com");
  url.searchParams.delete("lang");
  // Même normalisation que la canonical : toujours https://www.mtbbtp.com
  if (/(^|\.)mtbbtp\.com$/i.test(url.hostname)) {
    url.protocol = "https:";
    url.hostname = "www.mtbbtp.com";
    url.port = "";
  }
  const cleanBase = url.toString().replace(/\?$/, "");

  const variants: { hreflang: string; href: string }[] = [
    { hreflang: "fr", href: cleanBase },
    { hreflang: "en", href: `${cleanBase}${cleanBase.includes("?") ? "&" : "?"}lang=en` },
    { hreflang: "x-default", href: cleanBase },
  ];

  variants.forEach(({ hreflang, href }) => {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = hreflang;
    link.href = href;
    link.setAttribute(HREFLANG_MARKER, "true");
    document.head.appendChild(link);
  });
};

/**
 * Inject (or replace) page-scoped JSON-LD scripts in <head>.
 * Tagged with data-mtb-jsonld so we only manage scripts we own.
 */
const updateJsonLd = (data?: object | object[]) => {
  // Remove previously injected blocks
  document.head
    .querySelectorAll(`script[data-${JSON_LD_ID}="true"]`)
    .forEach((node) => node.remove());

  if (!data) return;

  const blocks = Array.isArray(data) ? data : [data];
  blocks.forEach((block) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset[
      // dataset uses camelCase; results in data-mtb-jsonld
      "mtbJsonld"
    ] = "true";
    script.text = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

/** Remove previously injected article:* tags so each page starts clean. */
const clearArticleTags = () => {
  document.head
    .querySelectorAll(`meta[${ARTICLE_TAG_MARKER}="true"]`)
    .forEach((node) => node.remove());
};

const appendArticleMeta = (property: string, content: string) => {
  const el = document.createElement("meta");
  el.setAttribute("property", property);
  el.setAttribute("content", content);
  el.setAttribute(ARTICLE_TAG_MARKER, "true");
  document.head.appendChild(el);
};

const LOCALE_MAP: Record<string, string> = {
  fr: "fr_CD",
  en: "en_US",
};

const ALTERNATE_LOCALE_MAP: Record<string, string> = {
  fr: "en_US",
  en: "fr_CD",
};

const Seo = ({ 
  title, 
  description, 
  image = DEFAULT_IMAGE,
  imageAlt,
  url,
  type = "website",
  locale,
  siteName = DEFAULT_SITE_NAME,
  article,
  twitterSite,
  jsonLd,
  noindex = false,
}: SeoProps) => {
  const { language } = useLanguage();
  const resolvedLocale = locale || LOCALE_MAP[language] || "fr_CD";
  const alternateLocale = ALTERNATE_LOCALE_MAP[language] || "en_US";

  useEffect(() => {
    const rawUrl = url || window.location.href;
    // Self-referencing canonical on the clean URL (no `lang` param) so that
    // `/about`, `/about?lang=fr` and `/about?lang=en` consolidate into one URL.
    // Language variants are declared through hreflang alternates instead.
    const _u = new URL(rawUrl, window.location.origin);
    _u.searchParams.delete("lang");
    // Toutes les canonical / og:url sont normalisées sur https://www.mtbbtp.com
    // (jamais la variante sans « www », ni un domaine de prévisualisation).
    if (/(^|\.)mtbbtp\.com$/i.test(_u.hostname)) {
      _u.protocol = "https:";
      _u.hostname = "www.mtbbtp.com";
      _u.port = "";
    }
    const cleanBase = _u.toString().replace(/\?$/, "");
    // Chaque version linguistique possède sa propre canonical auto-référencée :
    // FR (version principale) sur l'URL propre, EN sur l'URL `?lang=en`.
    const currentUrl =
      language === "en"
        ? `${cleanBase}${cleanBase.includes("?") ? "&" : "?"}lang=en`
        : cleanBase;

    const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
    const absoluteImage = toAbsoluteUrl(image) || DEFAULT_IMAGE;
    const resolvedImageAlt = imageAlt || title;

    // Basic meta tags
    document.title = fullTitle;
    updateMetaTag('meta[name="description"]', {
      name: "description",
      content: description,
    });

    // Open Graph tags
    updateMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    });
    updateMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    updateMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    updateMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: currentUrl,
    });
    updateMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: absoluteImage,
    });
    updateMetaTag('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: absoluteImage,
    });
    updateMetaTag('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: resolvedImageAlt,
    });
    updateMetaTag('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    updateMetaTag('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    updateMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: siteName,
    });
    updateMetaTag('meta[property="og:locale"]', {
      property: "og:locale",
      content: resolvedLocale,
    });
    updateMetaTag('meta[property="og:locale:alternate"]', {
      property: "og:locale:alternate",
      content: alternateLocale,
    });

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    updateMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    });
    updateMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    updateMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: absoluteImage,
    });
    updateMetaTag('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: resolvedImageAlt,
    });
    if (twitterSite) {
      updateMetaTag('meta[name="twitter:site"]', {
        name: "twitter:site",
        content: twitterSite,
      });
      updateMetaTag('meta[name="twitter:creator"]', {
        name: "twitter:creator",
        content: twitterSite,
      });
    }

    // Article-specific OG tags (Facebook/LinkedIn rich previews)
    clearArticleTags();
    if (type === "article" && article) {
      if (article.publishedTime) {
        appendArticleMeta("article:published_time", article.publishedTime);
      }
      if (article.modifiedTime) {
        appendArticleMeta("article:modified_time", article.modifiedTime);
      }
      if (article.author) {
        appendArticleMeta("article:author", article.author);
      }
      if (article.section) {
        appendArticleMeta("article:section", article.section);
      }
      (article.tags || [])
        .filter((tag): tag is string => Boolean(tag && tag.trim()))
        .forEach((tag) => appendArticleMeta("article:tag", tag));
    }

    // Directive robots : noindex sur les pages non indexables
    updateMetaTag('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow",
    });

    // Canonical URL
    updateLinkTag("canonical", currentUrl);

    // Hreflang alternate links for multilingual SEO (base sans paramètre `lang`)
    updateHreflangLinks(cleanBase);

    // JSON-LD structured data
    updateJsonLd(jsonLd);
  }, [title, description, image, imageAlt, url, type, resolvedLocale, alternateLocale, siteName, article, twitterSite, jsonLd, language, noindex]);

  // Cleanup JSON-LD + article tags on unmount so the next page starts fresh
  useEffect(() => {
    return () => {
      updateJsonLd(undefined);
      clearArticleTags();
      document.head
        .querySelectorAll(`link[${HREFLANG_MARKER}="true"]`)
        .forEach((node) => node.remove());
    };
  }, []);

  return null;
};

export default Seo;

